import sortBy from "lodash/sortBy";
import some from "lodash/some";

//
// Action types
//

export const INITIALIZE_MAP_PANEL = "INITIALIZE_MAP_PANEL";
export const SHOW_MAP_PANEL = "SHOW_MAP_PANEL";
export const HIDE_MAP_PANEL = "HIDE_MAP_PANEL";
export const OPEN_MAP_PANEL = "OPEN_MAP_PANEL";
export const CLOSE_MAP_PANEL = "CLOSE_MAP_PANEL";
export const CLOSE_MAP_LAST_PANEL = "CLOSE_MAP_LAST_PANEL";
export const REMOVE_MAP_PANEL = "REMOVE_MAP_PANEL";
export const RESTORE_MAP_PANEL = "RESTORE_MAP_PANEL";

export const SET_MAP_PANEL_ELEMENT = "SET_MAP_PANEL_ELEMENT";
export const SET_MAP_PANEL_HEADER = "SET_MAP_PANEL_HEADER";
export const SET_MAP_PANEL_MAP_ELEMENT = "SET_MAP_PANEL_MAP_ELEMENT";

export const SET_MAP_PANEL_ACTIONS = "SET_MAP_PANEL_ACTIONS";
export const REMOVE_MAP_PANEL_ACTIONS = "REMOVE_MAP_PANEL_ACTIONS";

//
// Action creators
//

export function initializeMapPanel(mapKey, id) {
  return {
    type: INITIALIZE_MAP_PANEL,
    mapKey,
    id
  };
}

export function showMapPanel(mapKey, id) {
  return {
    type: SHOW_MAP_PANEL,
    mapKey,
    id
  };
}

export function hideMapPanel(mapKey, id) {
  return {
    type: HIDE_MAP_PANEL,
    mapKey,
    id
  };
}

export function openMapPanel(mapKey, id) {
  return {
    type: OPEN_MAP_PANEL,
    mapKey,
    id
  };
}

export function closeMapPanel(mapKey, id) {
  return {
    type: CLOSE_MAP_PANEL,
    mapKey,
    id
  };
}

export function closeMapLastPanel(mapKey) {
  return {
    type: CLOSE_MAP_LAST_PANEL,
    mapKey
  };
}

export function removeMapPanel(mapKey, id) {
  return {
    type: REMOVE_MAP_PANEL,
    mapKey,
    id
  };
}

export function restoreMapPanel(mapKey, id) {
  return {
    type: RESTORE_MAP_PANEL,
    mapKey,
    id
  };
}

export function setMapPanelElement(mapKey, id, element) {
  return {
    type: SET_MAP_PANEL_ELEMENT,
    mapKey,
    id,
    element
  };
}

export function setMapPanelHeader(mapKey, id, element) {
  return {
    type: SET_MAP_PANEL_HEADER,
    mapKey,
    id,
    element
  };
}

export function setMapPanelMapElement(mapKey, id, element) {
  return {
    type: SET_MAP_PANEL_MAP_ELEMENT,
    mapKey,
    id,
    element
  };
}

export function setMapPanelActions(mapKey, id, element) {
  return {
    type: SET_MAP_PANEL_ACTIONS,
    mapKey,
    id,
    element
  };
}

export function removeMapPanelActions(mapKey, id) {
  return {
    type: REMOVE_MAP_PANEL_ACTIONS,
    mapKey,
    id
  };
}

// Returns the provided state with every active panels position updated so that
// we have a consistent positions set.
//
function reorderMapPanelPositions(state) {
  const panels = sortBy(
    Object.values(state.activePanels),
    panel => panel.position
  );

  panels.forEach((panel, index) => {
    state.activePanels[panel.id].position = index;
  });

  return state;
}

function openLastActiveMapPanel(state) {
  // Don't do anything if we have no active panel
  if (!Object.keys(state.activePanels).length) return;

  // If a panel is already open, don't do anything
  if (some(state.activePanels, panel => panel.isShown)) return;

  // Get a sorted list of all panels
  const panels = sortBy(
    Object.values(state.activePanels),
    panel => panel.position
  );

  // Set the last panel as open
  const newActiveMapPanel = panels[panels.length - 1];

  newActiveMapPanel.isShown = true;
}

function initializeMapPanelHandler(state, id) {
  const newState = { ...state };
  if (!newState.activePanels) newState.activePanels = {};

  const position = Object.keys(newState.activePanels).length;
  const isShown = true;

  newState.activePanels[id] = { id, position, isShown };

  return newState;
}

function showMapPanelHandler(state, id) {
  const newState = { ...state };
  const targetMapPanel = newState.activePanels[id];

  targetMapPanel.isShown = true;

  Object.keys(newState.activePanels).forEach(id => {
    const panel = newState.activePanels[id];

    if (panel.id !== targetMapPanel.id) {
      panel.isShown = false;
    }
  });

  return reorderMapPanelPositions(newState);
}

function hideMapPanelHandler(state, id) {
  const newState = { ...state };
  const targetMapPanel = newState.activePanels[id];

  targetMapPanel.isShown = false;

  return reorderMapPanelPositions(newState);
}

function openMapPanelHandler(state, id) {
  const newState = { ...state };
  const targetMapPanel = newState.activePanels[id];

  targetMapPanel.isOpen = true;

  return reorderMapPanelPositions(newState);
}

function closeMapPanelHandler(state, id) {
  const newState = { ...state };
  const targetMapPanel = newState.activePanels[id];

  targetMapPanel.isOpen = false;

  return reorderMapPanelPositions(newState);
}

function closeMapLastPanelHandler(state) {
  const panelsCount = state.activePanels.length;

  if (!panelsCount) return state;

  const targetMapPanelId = state.activePanels[panelsCount - 1].id;

  return closeMapPanelHandler(state, targetMapPanelId);
}

function removeMapPanelHandler(state, id) {
  const newState = { ...state };
  delete newState.activePanels[id];

  openLastActiveMapPanel(newState);

  return reorderMapPanelPositions(newState);
}

function restoreMapPanelHandler(state, id) {
  const newState = { ...state };
  const targetMapPanel = newState.activePanels[id];

  Object.keys(newState.activePanels).forEach(id => {
    const panel = newState.activePanels[id];

    if (panel.position > targetMapPanel.position) {
      panel.shouldBeClosed = true;
    }
  });

  return newState;
}

function setMapPanelElementHandler(state, id, element) {
  const newState = { ...state };
  const targetMapPanel = newState.activePanels[id];

  targetMapPanel.element = element;

  return newState;
}

function setMapPanelHeaderHandler(state, id, element) {
  const newState = { ...state };
  const targetMapPanel = newState.activePanels[id];

  targetMapPanel.header = element;

  return newState;
}

function setMapPanelMapElementHandler(state, id, element) {
  const newState = { ...state };
  const targetMapPanel = newState.activePanels[id];

  targetMapPanel.mapElement = element;

  return newState;
}

function setMapPanelActionsHandler(state, id, element) {
  const newState = { ...state };
  const targetMapPanel = newState.activePanels[id];

  targetMapPanel.actions = element;

  return newState;
}

function removeMapPanelActionsHandler(state, id) {
  const newState = { ...state };
  const targetMapPanel = newState.activePanels[id];

  if (!targetMapPanel) return state;

  delete targetMapPanel.actions;

  return newState;
}

//
// Reducer and handlers
//
export function reduceMapPanels(state, action) {
  switch (action && action.type) {
    case INITIALIZE_MAP_PANEL:
      return initializeMapPanelHandler(state, action.id);

    case SHOW_MAP_PANEL:
      return showMapPanelHandler(state, action.id);

    case HIDE_MAP_PANEL:
      return hideMapPanelHandler(state, action.id);

    case OPEN_MAP_PANEL:
      return openMapPanelHandler(state, action.id);

    case CLOSE_MAP_PANEL:
      return closeMapPanelHandler(state, action.id);

    case CLOSE_MAP_LAST_PANEL:
      return closeMapLastPanelHandler(state);

    case REMOVE_MAP_PANEL:
      return removeMapPanelHandler(state, action.id);

    case RESTORE_MAP_PANEL:
      return restoreMapPanelHandler(state, action.id);

    case SET_MAP_PANEL_ELEMENT:
      return setMapPanelElementHandler(state, action.id, action.element);

    case SET_MAP_PANEL_HEADER:
      return setMapPanelHeaderHandler(state, action.id, action.header);

    case SET_MAP_PANEL_MAP_ELEMENT:
      return setMapPanelMapElementHandler(state, action.id, action.element);

    case SET_MAP_PANEL_ACTIONS:
      return setMapPanelActionsHandler(state, action.id, action.element);

    case REMOVE_MAP_PANEL_ACTIONS:
      return removeMapPanelActionsHandler(state, action.id);

    default:
      return state || { activePanels: {} };
  }
}
